import AccountPage from "../components/account/AccountPage";
import { useEffect, useState } from "react";
import {
  FaEnvelope,
  FaUser,
  FaCheckCircle,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaKey,
} from "react-icons/fa";
import api from "../api";

export default function UserDetail() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwords, setPasswords] = useState({
    old_password: "",
    new_password1: "",
    new_password2: "",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = user?.access;

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await api.get("/api/auth/user/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserData(res.data);
      } catch (err) {
        console.error("Unable to load profile:", err.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage("");
    setPasswordError("");

    if (!passwords.old_password || !passwords.new_password1 || !passwords.new_password2) {
      setPasswordError("Please complete all password fields.");
      return;
    }

    if (passwords.new_password1 !== passwords.new_password2) {
      setPasswordError("The new passwords do not match.");
      return;
    }

    if (passwords.new_password1.length < 8) {
      setPasswordError("Your new password must be at least 8 characters.");
      return;
    }

    try {
      setChangingPassword(true);

      await api.post(
        "/api/auth/password/change/",
        passwords,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPasswords({ old_password: "", new_password1: "", new_password2: "" });
      setPasswordMessage("Your password has been changed successfully.");
    } catch (err) {
      const data = err.response?.data;
      const firstError = data && typeof data === "object"
        ? Object.values(data).flat().find(Boolean)
        : null;
      setPasswordError(firstError || "Unable to change your password. Please check your current password and try again.");
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24 bg-[#F7F1E3] min-h-screen">
        <div className="animate-spin rounded-full h-14 w-14 border-4 border-[#D8D0BE] border-t-[#0F766E]" />
      </div>
    );
  }

  if (!token) {
    return <div className="text-center py-20 bg-[#F7F1E3] min-h-screen text-[#374151]">Please log in to view your profile.</div>;
  }

  if (!userData) {
    return <div className="text-center py-20 bg-[#F7F1E3] min-h-screen text-[#374151]">Unable to load your profile.</div>;
  }

  const initials = userData.first_name && userData.last_name
    ? `${userData.first_name[0]}${userData.last_name[0]}`
    : userData.username
    ? userData.username[0].toUpperCase()
    : "?";

  const inputClass = "w-full rounded-xl border border-[#DED5C7] bg-white px-4 py-3 pr-11 text-[#1F2937] outline-none transition focus:border-[#0F766E] focus:ring-4 focus:ring-[#0F766E]/10";

  return (
    <AccountPage title="My Profile">
      <div className="max-w-5xl mx-auto space-y-7">
        <section className="relative overflow-hidden rounded-3xl bg-[#0F766E] p-7 sm:p-9 text-white shadow-[0_18px_45px_rgba(15,118,110,.18)]">
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full border-[42px] border-[#F8F3E7]/10" />
          <div className="absolute -left-16 -bottom-24 h-52 w-52 rounded-full bg-[#F8F3E7]/5" />
          <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-5">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-[#F8F3E7] text-[#0F766E] text-3xl font-bold shadow-xl ring-4 ring-white/10">
              {initials}
            </div>
            <div className="text-center sm:text-left flex-1">
              <p className="text-xs font-semibold uppercase tracking-[.22em] text-[#CDE8E5]">A1 Rugs Account</p>
              <h2 className="mt-1 text-3xl sm:text-4xl font-bold">
                {userData.first_name || userData.last_name
                  ? `${userData.first_name || ""} ${userData.last_name || ""}`.trim()
                  : userData.username}
              </h2>
              <p className="mt-2 text-[#DDF1EF] break-all">{userData.email}</p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm text-[#E9F7F5]">
                <FaCheckCircle /> Account active
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-[#E9F7F5]">
              <FaShieldAlt /> Secure account
            </div>
          </div>
        </section>

        <div className="grid lg:grid-cols-2 gap-6">
          <section className="rounded-2xl border border-[#E5DDCC] bg-[#FFFDF8] p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F3F1] text-[#0F766E]"><FaUser /></div>
              <div><h3 className="text-xl font-semibold text-[#1F2937]">Personal Information</h3><p className="text-sm text-[#8A8172]">Your account details</p></div>
            </div>
            <div className="space-y-4">
              {[
                ["First Name", userData.first_name],
                ["Last Name", userData.last_name],
                ["Username", userData.username],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-[#F8F3E7]/60 px-4 py-3">
                  <p className="text-xs uppercase tracking-wider text-[#8A8172]">{label}</p>
                  <p className="mt-1 font-semibold text-[#1F2937]">{value || "-"}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-[#E5DDCC] bg-[#FFFDF8] p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F3F1] text-[#0F766E]"><FaEnvelope /></div>
              <div><h3 className="text-xl font-semibold text-[#1F2937]">Contact Details</h3><p className="text-sm text-[#8A8172]">How we reach you</p></div>
            </div>
            <div className="space-y-4">
              <div className="rounded-xl bg-[#F8F3E7]/60 px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-[#8A8172]">Email Address</p>
                <p className="mt-1 font-semibold text-[#1F2937] break-all">{userData.email || "-"}</p>
              </div>
              <div className="rounded-xl bg-[#F8F3E7]/60 px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-[#8A8172]">Account ID</p>
                <p className="mt-1 font-semibold text-[#1F2937]">{userData.pk || userData.id || "-"}</p>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-[#DDEBE9] bg-[#F1F9F8] px-4 py-3 text-sm text-[#115E59]">
                <FaCheckCircle /> Your email is connected to this account.
              </div>
            </div>
          </section>
        </div>

        <section className="rounded-2xl border border-[#E5DDCC] bg-[#FFFDF8] p-6 sm:p-7 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F3F1] text-[#0F766E]"><FaKey /></div>
              <div><h3 className="text-xl font-semibold text-[#1F2937]">Security</h3><p className="text-sm text-[#8A8172]">Keep your A1 Rugs account protected</p></div>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#E6F3F1] px-3 py-1.5 text-xs font-semibold text-[#115E59]"><FaLock /> Change password</span>
          </div>

          {passwordMessage && <div className="mb-5 rounded-xl border border-[#B7DED8] bg-[#ECF8F6] px-4 py-3 text-sm font-medium text-[#115E59]">{passwordMessage}</div>}
          {passwordError && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{passwordError}</div>}

          <form onSubmit={handlePasswordChange} className="grid md:grid-cols-3 gap-4">
            {[
              ["old_password", "Current password", "old"],
              ["new_password1", "New password", "new"],
              ["new_password2", "Confirm new password", "confirm"],
            ].map(([name, label, key]) => (
              <label key={name} className="block">
                <span className="mb-2 block text-sm font-semibold text-[#374151]">{label}</span>
                <div className="relative">
                  <input
                    type={showPasswords[key] ? "text" : "password"}
                    value={passwords[name]}
                    onChange={(e) => setPasswords((prev) => ({ ...prev, [name]: e.target.value }))}
                    className={inputClass}
                    autoComplete={key === "old" ? "current-password" : "new-password"}
                  />
                  <button type="button" onClick={() => togglePassword(key)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8172] hover:text-[#0F766E]" aria-label={`Show ${label}`}>
                    {showPasswords[key] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </label>
            ))}
            <div className="md:col-span-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
              <p className="text-xs text-[#8A8172]">Use at least 8 characters and avoid reusing an old password.</p>
              <button disabled={changingPassword} type="submit" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F766E] px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-[#0B5F59] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60">
                <FaLock /> {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </AccountPage>
  );
}
